import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import type { WhatsappChat, WhatsappMessage } from "../../types.ts";
import { dateLabelFor } from "../Chat.helpers.ts";
import { MessageBubble } from "./MessageBubble.tsx";

type MessagesPaneProps = {
  messages: WhatsappMessage[];
  chat: WhatsappChat;
  isLoading: boolean;
  hasMoreMessages?: boolean;
  olderLoading?: boolean;
  onLoadOlder?: () => Promise<void> | void;
};

type MessageRow =
  | { kind: "day"; key: string; label: string }
  | { kind: "message"; key: string; message: WhatsappMessage; showTail: boolean };

export function MessagesPane({
  messages,
  chat,
  isLoading,
  hasMoreMessages = false,
  olderLoading = false,
  onLoadOlder,
}: MessagesPaneProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const preserveScrollRef = useRef<{ height: number; top: number } | null>(null);
  const olderRequestRef = useRef(false);
  const rows = useMemo(() => buildMessageRows(messages, chat), [messages, chat]);

  const requestOlder = useCallback(() => {
    if (!hasMoreMessages || olderLoading || olderRequestRef.current || !onLoadOlder) return;
    const node = scrollRef.current;
    if (node) {
      preserveScrollRef.current = { height: node.scrollHeight, top: node.scrollTop };
    }
    olderRequestRef.current = true;
    try {
      Promise.resolve(onLoadOlder())
        .finally(() => {
          olderRequestRef.current = false;
        })
        .catch(() => undefined);
    } catch {
      olderRequestRef.current = false;
    }
  }, [hasMoreMessages, olderLoading, onLoadOlder]);

  useEffect(() => {
    olderRequestRef.current = false;
  }, [chat.chatJid]);

  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const preserve = preserveScrollRef.current;
    if (preserve) {
      node.scrollTop = node.scrollHeight - preserve.height + preserve.top;
      preserveScrollRef.current = null;
      return;
    }
    node.scrollTop = node.scrollHeight;
  }, [messages.length, chat.chatJid]);

  function handleScroll() {
    const node = scrollRef.current;
    if (!node || node.scrollTop > 32) return;
    requestOlder();
  }

  return (
    <div ref={scrollRef} className="wa-messages" id={`wa-scroll-${chat.chatJid}`} onScroll={handleScroll}>
      <div className="wa-messages-inner">
        {hasMoreMessages ? (
          <div className="wa-load-older-row">
            <button type="button" className="wa-load-older-btn" onClick={requestOlder} disabled={olderLoading}>
              {olderLoading ? "Carregando..." : "Carregar mensagens anteriores"}
            </button>
          </div>
        ) : null}
        {isLoading && !messages.length ? <div className="wa-loading">Carregando...</div> : null}
        {!isLoading && !messages.length ? <div className="wa-empty-msg">Sem mensagens nessa conversa.</div> : null}
        {rows.map((row) => (
          row.kind === "day"
            ? <div key={row.key} className="wa-day"><span>{row.label}</span></div>
            : <MessageBubble key={row.message.messageKey || row.key} message={row.message} chat={chat} showTail={row.showTail} />
        ))}
      </div>
    </div>
  );
}

function buildMessageRows(messages: WhatsappMessage[], chat: WhatsappChat): MessageRow[] {
  const rows: MessageRow[] = [];
  let lastDateKey = "";
  let lastFromMe: boolean | null = null;
  messages.forEach((message, index) => {
    const dateKey = dateLabelFor(message.tsMs || 0);
    if (dateKey !== lastDateKey) {
      rows.push({ kind: "day", key: `day-${dateKey}-${index}`, label: dateKey });
      lastDateKey = dateKey;
      lastFromMe = null;
    }
    const showTail = lastFromMe !== !!message.fromMe;
    rows.push({ kind: "message", key: `${chat.chatJid}-${message.messageKey || index}`, message, showTail });
    lastFromMe = !!message.fromMe;
  });
  return rows;
}
